import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Breadcrumbs from "../components/breadcrumbs/Breadcrumbs";
import { useCartStore } from "../store/cartStore";
import { addToCart, getCart, removeFromCart, transformProductsToCartItems } from "../services/cartService";
import Gallery from "../components/gallery/Gallery";
import ReviewSection from "../components/reviews/ReviewSection";
import { useProductStore } from "../store/productStore";
import Loader from "../components/loader/Loader";
import ArrowIconBody from "../assets/icons/ArrowIconBody";

// Импортируем избранное: store и сервисы
import { useFavoritesStore } from "../store/favoritesStore";
import { getAllFavorites, addToFavorites, removeFromFavorites } from "../services/favoritesService";
import BasketIcon from "../assets/icons/BasketIcon";
// Убрали старую логику остатков по городам - теперь используется система складов
import Notification from "../components/toast/Notification";

const ProductDetailPage = () => {
  const { id } = useParams();
  const productId = Number(id);
  const navigate = useNavigate();

  const {
    currentProduct: product,
    fetchProduct,
    loading,
    error,
  } = useProductStore();

  // Убрали старую логику остатков по городам - теперь используется система складов
  const [stock, setStock] = useState(0);

  const [isDescriptionOpen, setDescriptionOpen] = useState(false);
  const [notification, setNotification] = useState<{ id: number, text: string; status: "success" | "error" | "info" } | null>(null);

  const { items, addItem, updateItemQuantity, removeItem, setCart } = useCartStore();
  const isLoggedIn = !!localStorage.getItem("token");
  const cartItem = items.find((i) => i.id === productId);

  // Избранное
  const {
    items: favoriteItems,
    addItem: addFavorite,
    removeItem: removeFavorite,
    setItems: setFavorites,
  } = useFavoritesStore();

  const isFavorite = favoriteItems.some((item) => item.id === product?.id);

  // Загружаем продукт при монтировании
  useEffect(() => {
    fetchProduct(productId);
  }, [productId, fetchProduct]);

  useEffect(() => {
    if (product) {
      // Теперь используем stock_quantity из продукта или 0 по умолчанию
      setStock(product.stock_quantity || 0);
    }
  }, [product]);

  // Обновление корзины с сервера
  const refreshCartFromServer = async () => {
    try {
      const response = await getCart();
      setCart(transformProductsToCartItems(response.result));
    } catch (err) {
      console.error("Ошибка при обновлении корзины:", err);
    }
  };

  // Обновление избранного с сервера (для залогиненных)
  const refreshFavoritesFromServer = async () => {
    try {
      const response = await getAllFavorites();
      setFavorites(response.result);
    } catch (error) {
      console.error("Ошибка при обновлении избранного:", error);
    }
  };

  // Добавляем товар в корзину
  const handleAddToCart = async () => {
    if (!product) return;

    // Если нет в наличии:
    if (stock === 0) {
      setNotification({ id: Date.now(), text: `Нет в наличии`, status: "info" });
      return;
    }

    // Если уже в корзине и кол-во равно stock — нельзя добавить
    if (cartItem && cartItem.quantity && stock && cartItem.quantity >= stock) {
      setNotification({ id: Date.now(), text: `Нельзя добавить больше, чем ${stock} штук`, status: "info" });
      return;
    }

    if (isLoggedIn) {
      try {
        await addToCart(product.id, 1);
        await refreshCartFromServer();
      } catch (error) {
        console.error("Ошибка добавления в корзину:", error);
      }
    } else {
      if (cartItem) {
        updateItemQuantity(product.id, 1);
      } else {
        addItem(product);
      }
    }
  };

  // Уменьшаем количество товара в корзине
  const handleDecrement = async () => {
    if (!product || !cartItem) return;
    if (isLoggedIn) {
      try {
        if (cartItem.quantity && cartItem.quantity > 1) {
          await addToCart(product.id, -1);
        } else {
          await removeFromCart(product.id);
        }
        await refreshCartFromServer();
      } catch (error) {
        console.error("Ошибка при уменьшении количества товара:", error);
      }
    } else {
      if (cartItem.quantity && cartItem.quantity > 1) {
        updateItemQuantity(product.id, -1);
      } else {
        removeItem(product.id);
      }
    }
  };

  // Переключение избранного: добавляем, если товар не в избранном, и удаляем, если уже есть
  const handleFavoriteToggle = async () => {
    if (!product) return;

    if (isLoggedIn) {
      try {
        if (isFavorite) {
          await removeFromFavorites(product.id);
        } else {
          await addToFavorites(product.id);
        }
        await refreshFavoritesFromServer();
      } catch (error) {
        console.error("Ошибка обновления избранного:", error);
      }
    } else {
      if (isFavorite) {
        removeFavorite(product.id);
      } else {
        addFavorite(product);
      }
    }
  };

  if (loading) return <Loader />;
  if (error) return <p>{error}</p>;
  if (!product) return <p>Товар не найден</p>;

  const images = product.images || [];

  return (
    <div className="container mx-auto px-4 py-8">

      {notification && <Notification id={notification.id || Date.now()} text={notification.text} status={notification.status} duration={5000} />}

      {/* Хлебные крошки */}
      <Breadcrumbs forcedCategoryId={product.category_id} productName={product.name} />

      <div className="flex flex-col md:flex-row gap-8 mt-8">
        {/* Левая колонка: слайдер */}
        <div className="w-full md:w-1/2">
          <Gallery
            images={product.images}
            onFavoriteToggle={handleFavoriteToggle}
            isFavorite={isFavorite}
          />
        </div>

        {/* Правая колонка: информация о продукте */}
        <div className="w-full md:w-1/2">
          <h1 className="text-2xl md:text-3xl font-bold mb-2 text-[#1E1E1E]">{product.name}</h1>
          <div className="flex flex-wrap mb-4">
            {/* Можно добавить теги */}
          </div>
          <p className="flex items-start text-2xl md:text-4xl font-bold mb-4 leading-none">
            ₸<span className="text-3xl md:text-5xl leading-none">{product.price}</span>
          </p>
          <p className="text-gray-600 mb-2">Штрихкод: {product.sku || "N/A"}</p>

          {/* Кнопки для работы с корзиной */}
          {stock === 0 ? (
            <button className="py-2 px-2 bg-gray-200 text-gray-400 rounded cursor-not-allowed mt-4">
              Нет в наличии
            </button>
          ) : cartItem ? (
            <div className="flex items-center">
              <div className="inline-flex items-center space-x-2 border rounded">
                <button
                  className="px-4 py-2 font-bold text-black border-black hover:bg-gray-100"
                  onClick={() => handleDecrement()}
                >
                  -
                </button>
                <span className="px-2">{cartItem.quantity}</span>
                <button
                  className="px-4 py-2 font-bold text-black border-black hover:bg-gray-100"
                  onClick={() => handleAddToCart()}
                >
                  +
                </button>
              </div>

              <button onClick={() => navigate("/cart")} className="flex flex-row items-center ml-4 min-h-[42px] px-2 border rounded hover:bg-[#2C2C2C]/50">
                перейти в
                <div className="flex items-center justify-center w-6 h-6">
                  <BasketIcon
                    className="w-7 h-7"
                    isEmpty={false}
                    isActive={location.pathname === "/cart"}
                  />
                </div>
              </button>
            </div>
          ) : (
            <button
              className="py-2 px-2 bg-[#2C2C2C] text-white rounded hover:bg-gray-800"
              onClick={() => handleAddToCart()}
            >
              В корзину
            </button>
          )}

          {/* Блок с описанием товара */}
          <div
            className="mt-6 border border-gray-300 rounded-lg p-4 cursor-pointer"
            onClick={() => setDescriptionOpen(!isDescriptionOpen)}
          >
            <div className="flex justify-between items-center">
              <h3 className="text-lg font-bold">Описание</h3>
              {isDescriptionOpen ? (
                <div className="transform transition-transform -rotate-90">
                  <ArrowIconBody />
                </div>
              ) : (
                <div className="transform transition-transform rotate-90">
                  <ArrowIconBody />
                </div>
              )}
            </div>
            <div className={`mt-4 transition-all duration-300 overflow-hidden ${isDescriptionOpen ? "max-h-96" : "max-h-20"}`}>
              <div className="prose" dangerouslySetInnerHTML={{ __html: product.description }} />
            </div>
          </div>
        </div>
      </div>

      {/* Раздел отзывов */}
      <div className="mt-20">
        <ReviewSection productId={productId} />
      </div>
    </div>
  );
};

export default ProductDetailPage;
